/**
 * @file     formatAddress.ts
 * @readme   ./CLAUDE.md
 * @purpose  Trim a raw geocoded address ("307 Richmond Street, New Westminster,
 *           British Columbia V3L 4B7, Canada") down to the street line for the
 *           report hero, where the city already renders on its own line. The full
 *           address is kept verbatim in the disclosure + record note (citation).
 */

/**
 * Return just the street portion of a raw address: everything before the city /
 * province / postal / country tail. Idempotent and safe on already-clean input.
 */
export function formatStreetAddress(raw: string, city?: string | null): string {
  let s = (raw ?? '').trim();
  // Cut from the city onward when the city is present in the string.
  const c = city?.trim();
  if (c) {
    const i = s.toLowerCase().indexOf(`, ${c.toLowerCase()}`);
    if (i > 0) s = s.slice(0, i);
  }
  // Fallbacks: cut at the province, strip a trailing postal code and country.
  s = s.replace(/,\s*(british columbia|bc)\b.*$/i, '');
  s = s.replace(/,\s*canada\s*$/i, '');
  s = s.replace(/,?\s*[A-Za-z]\d[A-Za-z]\s*\d[A-Za-z]\d\s*$/i, '');
  return s.replace(/[,\s]+$/, '').trim();
}

/**
 * Street and city, with province, postal code and country removed.
 *
 * The evidence table needs a different trim from the hero. The hero renders the
 * city on its own line, so `formatStreetAddress` cuts the city off; a comp table
 * cannot, because comparable sales cross municipal lines and "New Westminster"
 * is doing real work in that row. What the table does not need is the province,
 * postal code and country repeated on every line: they are identical down the
 * whole column, and carrying them sets the min-content width of the widest
 * column, which is what pushes the numeric columns off the page.
 *
 * Handles both feed spellings, since one writes ", British Columbia V3L 5P4,
 * Canada" and the other writes " New Westminster BC V3L 5S2" with no comma
 * before the province.
 */
export function shortAddress(raw: string | null | undefined, localCity?: string | null): string | null {
  if (!raw) return null;
  const s = raw
    .replace(/,\s*canada\b/gi, '')
    // Postal code anywhere, not only at the end.
    .replace(/\b[A-Za-z]\d[A-Za-z]\s*\d[A-Za-z]\d\b/g, ' ')
    // Trailing (?!\w) rather than \b: a word boundary cannot follow the period
    // in "B.C.", so \b would silently leave that spelling in place.
    .replace(/\b(?:british\s+columbia|b\.c\.|bc)(?!\w)/gi, ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s*,\s*/g, ', ')
    .replace(/[,\s]+$/, '')
    .trim();
  if (!s) return null;

  // The subject's own city repeats on every row and says nothing. Drop it, and
  // keep it only where it earns its place: a comp in a different municipality,
  // which is exactly the row a reader needs to notice.
  if (localCity?.trim()) {
    const tail = new RegExp(`,\\s*${localCity.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`, 'i');
    const trimmed = s.replace(tail, '').trim();
    if (trimmed) return trimmed;
  }
  return s;
}

export interface SplitAddress {
  /** Unit / suite token ("307"), uppercased. Null when the address has none. */
  unit: string | null;
  /** The civic address without the unit ("48 Richmond Street, New Westminster"). */
  street: string;
}

// Canadian unit notation, all resolving to unit + civic number:
//   "307-48 Main St"  "#307-48 Main St"  "307 - 48 Main St"  "#307 48 Main St"
//   "Unit 307, 48 Main St"  "Suite 307 48 Main St"
// Each requires a DIGIT after the unit (the civic number), so a plain street
// address is never mistaken for a unit.
const UNIT_PATTERNS: RegExp[] = [
  /^\s*(?:unit|suite|ste\.?|apt\.?|apartment)\s*#?\s*([0-9A-Za-z]+)\s*(?:,|-|–|—)?\s*(?=\d)/i,
  /^\s*#?\s*([0-9A-Za-z]+)\s*(?:-|–|—)\s*(?=\d)/,
  /^\s*#\s*([0-9A-Za-z]+)\s+(?=\d)/,
];

/** Street-type words, used to tell a NUMBERED STREET from a civic number. */
const STREET_TYPE = /^(ave|avenue|st|street|rd|road|dr|drive|pl|place|crt|court|cres|crescent|way|blvd|boulevard|lane|ln|terr|terrace|close|gate|mews|row|sq|square|hwy|highway|trail|pkwy|parkway|diversion|connector)\b/i;

/**
 * Split a leading unit off an address. Geocoders read "307-48 Richmond Street"
 * as civic number 307 (a different building ~1km away), so the unit must be
 * removed before geocoding and re-attached for display.
 *
 * Also handles the MLS/VOW form, which separates unit and civic with a SPACE
 * ("2807 680 QUAYSIDE DRIVE"). That is ambiguous against a numbered street
 * ("13258 105 AVENUE" is civic 13258 on 105 Avenue, no unit), so the token after
 * the second number decides: a street type means there was no unit.
 */
export function splitUnitFromAddress(raw: string): SplitAddress {
  const s = (raw ?? '').trim();
  for (const re of UNIT_PATTERNS) {
    const m = s.match(re);
    if (m) return { unit: m[1].toUpperCase(), street: s.slice(m[0].length).trim() };
  }
  const spaced = s.match(/^\s*(\d+)\s+(\d+)\s+(\S+)/);
  if (spaced) {
    const [, unit, , third] = spaced;
    // "216 13258 105 AVENUE" (three numbers) is unit + civic + numbered street.
    // "13258 105 AVENUE" is civic + numbered street, so no unit is present.
    if (/^\d+$/.test(third) || !STREET_TYPE.test(third)) {
      return { unit, street: s.replace(/^\s*\d+\s+/, '').trim() };
    }
  }
  return { unit: null, street: s };
}

/** Re-attach a unit to a civic address in the canonical "307-48 Main St" form. */
export function withUnit(unit: string | null, address: string): string {
  return unit ? `${unit}-${address}` : address;
}

/**
 * Keep a unit the user typed when a geocoder returns only the civic building.
 * Photon/OSM (and Mapbox) have no strata unit, so selecting "1480 Howe Street"
 * would otherwise overwrite "4609-1480 Howe". If the geocoder already carried a
 * unit, leave that result alone rather than double-prefixing.
 */
export function mergeTypedUnit(typedInput: string, civicAddress: string): string {
  const typedUnit = splitUnitFromAddress(typedInput).unit;
  if (!typedUnit) return civicAddress;
  if (splitUnitFromAddress(civicAddress).unit) return civicAddress;
  return withUnit(typedUnit, civicAddress);
}

/**
 * Floor from a unit token: "2807" -> 28, "307" -> 3, "12" -> 1, "PH2" -> null.
 * A 28th-floor tower unit and a 3rd-floor walk-up are different products even at
 * the same size and bed count (measured ~29% higher $/sqft for floors 15+ in New
 * Westminster), so the engine weights by floor proximity. Digits only: lettered
 * tokens (PH, TH) are too ambiguous to guess.
 */
export function floorFromUnit(unit: string | null): number | null {
  if (!unit || !/^\d+$/.test(unit)) return null;
  const floor = unit.length >= 4 ? Number(unit.slice(0, 2)) : unit.length === 3 ? Number(unit[0]) : 1;
  return floor > 0 && floor <= 99 ? floor : null;
}

/** Floor from a full address, via its unit token. Null when there is no unit. */
export function floorFromAddress(address: string | null | undefined): number | null {
  if (!address) return null;
  return floorFromUnit(splitUnitFromAddress(address).unit);
}
